/**
 * O recorte que a foto congelou, escrito por extenso.
 *
 * **Por que não são os chips de filtro ativo da tela de Menções.** Lá o
 * chip é um botão: clicar remove aquela dimensão. Aqui ele é ESTÁTICO —
 * a foto do relatório não se edita depois de tirada, e um chip que
 * acendesse no hover prometeria uma interação que não existe. Por isso
 * a classe é própria (sem `:hover`, sem `<button>`) e a cor é neutra: a
 * paleta de polaridade aqui leria como "este relatório é negativo", que
 * não é o que o chip diz.
 *
 * **Dois momentos, e a distinção importa.** Antes de gerar, o recorte
 * ainda é editável — mas a única edição que a tela oferece é "limpar
 * tudo" (`onClear`), porque tirar uma dimensão de um recorte que veio
 * de outra tela produziria um filtro que não corresponde a nenhuma
 * lista que a pessoa viu. Depois de gerado, a foto é imutável e não há
 * botão nenhum.
 *
 * **De onde saem os NOMES** (desde 2026-08-28): de
 * `generation_criteria_labels`, a grafia congelada no momento da
 * geração — nunca do catálogo vivo. É o que faz um relatório salvo
 * continuar dizendo "Sustentabilidade" depois de o tema virar "ESG".
 * Sem rótulo (relatório antigo, ou id que sumiu do catálogo), a tela
 * cai no derivado: "3 temas" em vez de nomes, jamais "None".
 *
 * Aparece em três lugares, e nos três pela mesma razão — desde que a
 * trava de execução compara o recorte inteiro, dois rascunhos podem
 * diferir SÓ na busca, e sem isto seriam duas linhas idênticas na tela.
 */

import {
  APPROACH_CHOICES,
  FORMAT_CHOICES,
  MEDIA_TYPE_CHOICES,
  NO_VALUE,
  SOURCE_CHOICES,
  TIER_CHOICES,
} from "@/features/mentions/mention-filter-criteria";
import { polarityLabel } from "@/shared/types/polarity";
import { formatCurrencyFull } from "@/shared/utils/format-currency";
import type {
  GenerationCriteria,
  GenerationCriteriaLabels,
} from "@/shared/api/api-client";
import {
  RECORTE_DIMENSIONS,
  type RecorteDimension,
  type ReportRecorte,
} from "@/features/reports/report-recorte";

/** "Busca: "x"" — e, fora do padrão, onde ela procura. */
function searchLabel(q: string, field: string | undefined): string {
  const base = `Busca: "${q}"`;
  if (field === "title") return `${base} · só no título`;
  if (field === "content") return `${base} · só no conteúdo`;
  return base;
}


/** Os campos de NOMES que canal e porte tinham na criteria antes de
 *  2026-11-03. */
const LEGACY_NAME_CHIPS: Partial<
  Record<RecorteDimension, { field: string; excludeField: string }>
> = {
  channels: { field: "channels", excludeField: "exclude_channels" },
  influencerTypes: {
    field: "influencer_types",
    excludeField: "exclude_influencer_types",
  },
};

/** Um chip. `key` é `dimensão:valores` porque o TEXTO duplicaria entre
 *  dimensões — "Institucional" pode ser tema e grupo-tema —, e `title`
 *  é o tooltip com a lista inteira quando ela não cabe. */

export type CriteriaChip = { key: string; label: string; title?: string };

/** Como cada dimensão se chama e onde estão seus rótulos. A ordem aqui
 *  é a ordem dos chips. */
const CHIP_LABELS: Record<
  RecorteDimension,
  { singular: string; plural: string; labelKey?: keyof GenerationCriteriaLabels }
> = {
  polarities: { singular: "polaridade", plural: "polaridades" },
  approaches: { singular: "abordagem", plural: "abordagens" },
  sources: { singular: "meio", plural: "meios" },
  channels: { singular: "canal", plural: "canais", labelKey: "channels" },
  vehicles: { singular: "veículo", plural: "veículos", labelKey: "vehicles" },
  tiers: { singular: "tier", plural: "tiers" },
  formats: { singular: "formato", plural: "formatos" },
  mediaTypes: { singular: "tipo de mídia", plural: "tipos de mídia" },
  influencerTypes: { singular: "porte", plural: "portes", labelKey: "publisher_tiers" },
  clippingServices: { singular: "clipadora", plural: "clipadoras" },
  brands: { singular: "marca", plural: "marcas", labelKey: "brands" },
  themes: { singular: "subtema", plural: "subtemas", labelKey: "themes" },
  themeGroups: {
    singular: "tema",
    plural: "temas",
    labelKey: "theme_groups",
  },
  spokespersons: {
    singular: "porta-voz",
    plural: "porta-vozes",
    labelKey: "spokespersons",
  },
  publisherGroups: {
    singular: "grupo de publicador",
    plural: "grupos de publicador",
    labelKey: "publisher_groups",
  },
  keyMessages: {
    singular: "mensagem-chave",
    plural: "mensagens-chave",
    labelKey: "key_messages",
  },
};

/** Quantos nomes cabem num chip antes de virar "+N". A elipse do CSS
 *  é a rede de segurança (e só passou a funcionar quando o chip virou
 *  `inline-block` — com `inline-flex` o texto era cortado no meio da
 *  palavra, sem reticências); o "+N" é a informação: "mais 5" diz algo
 *  que "..." não diz. */
const MAX_NAMES_PER_CHIP = 3;

/** A partir de quantos caracteres o chip provavelmente elipsa (320px de
 *  largura, `--font-micro`). Estimativa de propósito: errar pra mais
 *  custa um tooltip redundante; errar pra menos esconde o texto sem
 *  saída. */
const CHIP_ELLIPSIS_HINT = 44;

/** As dimensões cujo vocabulário a tela tem em memória. As de fora
 *  desta tabela — canal, porte de influenciador — guardam o próprio
 *  texto legível, então o valor JÁ É o rótulo. */
const CLOSED_VOCABULARY: Partial<
  Record<RecorteDimension, Array<{ value: string; label: string }>>
> = {
  approaches: APPROACH_CHOICES,
  sources: SOURCE_CHOICES,
  tiers: TIER_CHOICES,
  formats: FORMAT_CHOICES,
  mediaTypes: [...MEDIA_TYPE_CHOICES, { value: NO_VALUE, label: "Não informado" }],
  // O valor é o nome da clipadora; só o legado migrado tem rótulo à parte.
  clippingServices: [{ value: "legado", label: "Legado (migrado)" }],
};

/**
 * O nome de um valor, ou `null` quando não há nome nenhum.
 *
 * `null` acontece só nas dimensões de ID, e por duas razões legítimas:
 * a foto é anterior aos rótulos congelados (2026-08-28), ou o id sumiu
 * do catálogo entre a geração e a leitura. Nos dois casos o chamador
 * mostra a CONTAGEM — "2 marcas" — e nunca o id: um número cru no lugar
 * de um nome não informa nada e ainda parece defeito.
 *
 * As dimensões de valor nunca voltam `null`: elas guardam o próprio
 * texto, então o valor já é o rótulo mesmo sem vocabulário conhecido
 * (canal, porte de influenciador).
 */
function nameOf(
  dimension: RecorteDimension,
  value: string,
  labels: GenerationCriteriaLabels | null | undefined
): string | null {
  if (dimension === "polarities") return polarityLabel(value);
  if (dimension === "keyMessages" && value === NO_VALUE) return "Sem mensagem-chave";
  if (dimension === "themes" && value === NO_VALUE) return "Sem subtema";
  const fechado = CLOSED_VOCABULARY[dimension];
  if (fechado) {
    return fechado.find(choice => choice.value === value)?.label ?? value;
  }
  const key = CHIP_LABELS[dimension].labelKey;
  if (!key) return value;
  const mapa = labels?.[key] as Record<string, string> | undefined;
  return mapa?.[value] ?? null;
}

function chipFor(
  dimension: RecorteDimension,
  values: string[],
  exclude: boolean,
  labels: GenerationCriteriaLabels | null | undefined
): CriteriaChip {
  const nomes = values
    .map(value => nameOf(dimension, value, labels))
    .filter((nome): nome is string => nome !== null);
  const semNome = values.length - nomes.length;
  const { singular, plural } = CHIP_LABELS[dimension];
  const prefixo = exclude ? "Exceto " : "";

  // O chip é uma frase solta, não parte de outra — começa com
  // maiúscula. "Exceto marcas: …" e "Marcas: …", nunca "marcas: …".
  const nome = values.length === 1 ? singular : plural;
  const titulo = prefixo
    ? `${prefixo}${nome}`
    : nome.charAt(0).toUpperCase() + nome.slice(1);

  // Nada resolveu: foto anterior aos rótulos, ou catálogo apagado. A
  // contagem é a única coisa verdadeira que ainda se pode dizer, e é
  // melhor que uma lista de ids que parece um erro.
  if (nomes.length === 0) {
    return {
      key: `${dimension}:${values.join("|")}`,
      label: `${titulo}: ${values.length} ${
        values.length === 1 ? "selecionada" : "selecionadas"
      }`,
    };
  }

  const mostrados = nomes.slice(0, MAX_NAMES_PER_CHIP);
  const escondidos = nomes.length - mostrados.length + semNome;
  const texto =
    escondidos > 0
      ? `${mostrados.join(", ")} +${escondidos}`
      : mostrados.join(", ");
  const label = `${titulo}: ${texto}`;
  const inteiro = `${titulo}: ${nomes.join(", ")}${
    semNome > 0 ? ` +${semNome} sem nome` : ""
  }`;
  return {
    key: `${dimension}:${values.join("|")}`,
    label,
    // O tooltip só existe quando há algo escondido — um `title` que
    // repete o texto visível é ruído pra leitor de tela. Duas formas de
    // esconder: o "+N" acima, e a elipse do CSS quando três nomes
    // longos estouram os 320px do chip.
    title:
      inteiro !== label || label.length > CHIP_ELLIPSIS_HINT
        ? inteiro
        : undefined,
  };
}

/** O chip do escopo: de quem são as menções da foto. Só aparece fora
 *  do padrão — "só a empresa" é o que todo relatório sempre foi. */
function scopeChip(scope: string): CriteriaChip {
  return {
    key: "scope",
    label:
      scope === "competitors"
        ? "Menções dos concorrentes"
        : "Menções da empresa e dos concorrentes",
  };
}

/** O chip da revisão: o valor É o rótulo. */
function reviewedChip(reviewed: boolean): CriteriaChip {
  return {
    key: "reviewed",
    label: reviewed ? "Só revisadas" : "Só não revisadas",
  };
}

/** O chip da citação no título (só imprensa): o valor É o rótulo. */
function titleCitationChip(titleCitation: boolean): CriteriaChip {
  return {
    key: "titleCitation",
    label: titleCitation ? "Citação no título: com" : "Citação no título: sem",
  };
}

/** Os dois sim/não de 2026-11-01: o valor É o rótulo. */
function themeHighlightChip(themeHighlight: boolean): CriteriaChip {
  return {
    key: "themeHighlight",
    label: themeHighlight ? "Subtema em destaque: com" : "Subtema em destaque: sem",
  };
}

function sharedPostChip(sharedPost: boolean): CriteriaChip {
  return {
    key: "sharedPost",
    label: sharedPost ? "Post em mais de um cliente" : "Post só neste cliente",
  };
}

/** O chip da lista COLADA (2026-10-03). O texto é a contagem — a
 *  lista inteira de links não cabe num chip —, e o tooltip traz a
 *  AMOSTRA de referências congeladas quando a foto já existe
 *  (`labels.posts`, até 500), com "e mais N" pro resto. Antes de gerar
 *  só há o número da lista; a contagem vem como dica da tela de
 *  menções e pode faltar. */
function postsChip(
  total: number | null,
  labels?: GenerationCriteriaLabels | null
): CriteriaChip {
  const referencias = Object.values(labels?.posts ?? {}).filter(
    ref => typeof ref === "string" && ref !== ""
  );
  const resto = total !== null ? total - referencias.length : 0;
  return {
    key: "posts",
    label:
      total !== null
        ? `Links/códigos colados: ${total}`
        : "Links/códigos colados",
    title:
      referencias.length > 0
        ? `${referencias.join("\n")}${resto > 0 ? `\n… e mais ${resto}` : ""}`
        : undefined,
  };
}

/** Um chip por UPLOAD do recorte (2026-10-17). O nome é o do arquivo
 *  congelado na foto (`labels.uploads`); antes de gerar não há rótulo, e
 *  o chip diz "Upload #N" — a URL só carrega o id. */
function uploadChips(
  ids: number[],
  labels?: GenerationCriteriaLabels | null
): CriteriaChip[] {
  return ids.map(id => ({
    key: `upload:${id}`,
    label: `Upload: ${labels?.uploads?.[String(id)] || `Upload #${id}`}`,
  }));
}

/**
 * As dimensões de um recorte ainda não gerado (veio da querystring).
 *
 * **Marca fica de fora**, e não é esquecimento: ela é a única dimensão
 * que o formulário do relatório EDITA, e o seletor logo acima já mostra
 * quais estão escolhidas, por nome. O chip repetiria a informação — e
 * pior, repetiria como ID: a foto ainda não existe, então não há rótulo
 * congelado pra consultar, e a pessoa que acabou de escolher "Enel,
 * Corsan" leria "marcas: 4, 2".
 *
 * As outras dimensões de catálogo (tema, porta-voz…) mostram id aqui
 * pelo mesmo motivo, e ficam assim: elas não têm controle nesta tela, e
 * quem tem o nome é a tela de menções — de onde a pessoa acabou de vir,
 * com o recorte fresco na cabeça. Depois de gerar, o rótulo congelado
 * resolve os nomes de vez.
 */
export function recorteChips(
  recorte: ReportRecorte | null,
  labels?: GenerationCriteriaLabels | null
): CriteriaChip[] {
  if (!recorte) return [];
  const chips: CriteriaChip[] = [];
  for (const dimension of RECORTE_DIMENSIONS) {
    if (dimension.key === "brands") continue;
    const facet = recorte.facets[dimension.key];
    if (facet.values.length === 0) continue;
    chips.push(chipFor(dimension.key, facet.values, facet.exclude, labels));
  }
  if (recorte.q) {
    chips.push({ key: "q", label: searchLabel(recorte.q, recorte.qIn) });
  }
  if (recorte.minAdValue !== null) {
    chips.push({
      key: "minAdValue",
      label: `Valoração ≥ ${formatCurrencyFull(recorte.minAdValue)}`,
    });
  }
  if (recorte.referenceSet !== null) {
    chips.push(postsChip(recorte.referenceSet.postCount, labels));
  }
  if (recorte.reviewed !== null) chips.push(reviewedChip(recorte.reviewed));
  if (recorte.titleCitation !== null) {
    chips.push(titleCitationChip(recorte.titleCitation));
  }
  if (recorte.themeHighlight !== null) {
    chips.push(themeHighlightChip(recorte.themeHighlight));
  }
  if (recorte.sharedPost !== null) chips.push(sharedPostChip(recorte.sharedPost));
  chips.push(...uploadChips(recorte.uploadIds, labels));
  if (recorte.scope !== "own") chips.push(scopeChip(recorte.scope));
  return chips;
}

/**
 * As dimensões de uma foto já congelada.
 *
 * Lê as duas grafias: as listas da v3 e as escalares da v2
 * (`polarity`/`approach`/`brand_id`), que continuam nos relatórios
 * salvos antes de 2026-08-28. Chave desconhecida é ignorada — é o que
 * deixa o formato crescer sem quebrar tela antiga.
 */
export function criteriaChips(
  criteria: GenerationCriteria | null | undefined,
  labels?: GenerationCriteriaLabels | null
): CriteriaChip[] {
  if (!criteria) return [];
  const bruto = criteria as unknown as Record<string, unknown>;
  const chips: CriteriaChip[] = [];

  for (const dimension of RECORTE_DIMENSIONS) {
    const lista = bruto[dimension.field];
    const values = Array.isArray(lista) ? lista.map(String) : [];
    // Canal e porte eram NOMES até 2026-11-03: a amostra antiga traz a
    // lista na grafia velha, e o nome é o próprio rótulo.
    const antigo = LEGACY_NAME_CHIPS[dimension.key];
    if (antigo && !Array.isArray(lista) && Array.isArray(bruto[antigo.field])) {
      const nomes = (bruto[antigo.field] as unknown[]).map(String);
      if (nomes.length) {
        const chaveRotulos = CHIP_LABELS[dimension.key].labelKey as "channels" | "publisher_tiers";
        chips.push(
          chipFor(dimension.key, nomes, bruto[antigo.excludeField] === true, {
            ...labels,
            [chaveRotulos]: Object.fromEntries(nomes.map(nome => [nome, nome])),
          })
        );
      }
      continue;
    }
    // O "Sem mensagem-chave" vem em flag própria na criteria.
    if ("noneField" in dimension && bruto[dimension.noneField] === true) {
      values.push(NO_VALUE);
    }
    if (values.length === 0) continue;
    chips.push(
      chipFor(dimension.key, values, bruto[dimension.excludeField] === true, labels)
    );
  }

  // As escalares da v2, e só quando a plural correspondente não veio:
  // uma criteria v3 nunca tem as duas grafias, e se tivesse, a plural
  // é a corrente.
  for (const [campo, dimension] of [
    ["brand_id", "brands"],
    ["polarity", "polarities"],
    ["approach", "approaches"],
  ] as const) {
    const valor = bruto[campo];
    if (valor === undefined || valor === null) continue;
    if (chips.some(chip => chip.key.startsWith(`${dimension}:`))) continue;
    chips.push(chipFor(dimension, [String(valor)], false, labels));
  }

  if (typeof criteria.q === "string" && criteria.q) {
    chips.push({ key: "q", label: searchLabel(criteria.q, criteria.q_in) });
  }
  // Sem rótulo congelado: o valor É o rótulo. As outras dimensões
  // guardam id e precisam da grafia da época; um número em reais
  // significa hoje o que significava na geração.
  if (typeof criteria.min_ad_value === "number") {
    chips.push({
      key: "minAdValue",
      label: `Valoração ≥ ${formatCurrencyFull(criteria.min_ad_value)}`,
    });
  }
  // A foto guarda TODOS os ids; o rótulo, uma amostra e o total.
  const colados = bruto.raw_post_ids;
  if (Array.isArray(colados) && colados.length > 0) {
    chips.push(postsChip(labels?.posts_total ?? colados.length, labels));
  }
  if (typeof criteria.reviewed === "boolean") {
    chips.push(reviewedChip(criteria.reviewed));
  }
  if (typeof criteria.title_citation === "boolean") {
    chips.push(titleCitationChip(criteria.title_citation));
  }
  if (typeof criteria.theme_highlight === "boolean") {
    chips.push(themeHighlightChip(criteria.theme_highlight));
  }
  if (typeof criteria.shared_post === "boolean") {
    chips.push(sharedPostChip(criteria.shared_post));
  }
  const uploads = bruto.upload_ids;
  if (Array.isArray(uploads)) {
    chips.push(
      ...uploadChips(
        uploads.map(Number).filter(id => Number.isInteger(id)),
        labels
      )
    );
  }
  if (criteria.scope && criteria.scope !== "own") {
    chips.push(scopeChip(criteria.scope));
  }
  return chips;
}

export function ReportCriteriaChips({
  items,
  prefix,
  onClear
}: {
  items: CriteriaChip[];
  /** Texto curto antes dos chips ("Recorte vindo de menções"). */
  prefix?: string;
  /** Quando existe, vira o ÚNICO botão da barra: a saída é limpar o
   *  recorte inteiro, não tirar uma dimensão de cada vez. */
  onClear?: () => void;
}) {
  if (items.length === 0) return null;
  return (
    <div className="report-criteria-chips">
      {prefix && <span className="report-criteria-label">{prefix}</span>}
      {items.map(item => (
        <span
          key={item.key}
          className="report-criteria-chip"
          title={item.title}
        >
          {item.label}
        </span>
      ))}
      {onClear && (
        <button type="button" className="text-button" onClick={onClear}>
          Limpar recorte
        </button>
      )}
    </div>
  );
}
